import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { FloatingWhatsApp } from '@/components/FloatingWhatsApp';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowLeft, ArrowRight } from 'lucide-react';

const servicesData = {
  'google-ads': {
    name: 'Google Ads',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/c/c7/Google_Ads_logo.svg',
    tagline: 'Turn your advertising budget into profitable customer acquisition',
    description: 'Google Ads is Google\'s pay-per-click advertising platform that lets businesses display targeted ads across Search, YouTube, Gmail, and the Display Network to reach customers at the exact moment they\'re looking for what you offer.',
    iconColor: '#34A853', // GREEN for checklist icons
    features: [
      {
        title: 'Campaign Strategy & Setup',
        description: 'Develop a custom, data-driven campaign strategy aligned with your business goals.',
      },
      {
        title: 'Multi-Format Campaigns',
        description: 'Execute campaign setups across Search, Shopping, Display, and Video.',
      },
      {
        title: 'Keyword Research',
        description: 'Research and refine high-value keywords and negative keywords.',
      },
      {
        title: 'Bid Optimization',
        description: 'Optimize bids and budgets daily to maximize your Return on Ad Spend (ROAS).',
      },
      {
        title: 'Performance Analysis',
        description: 'Analyze performance data to identify clear, actionable insights for growth.',
      },
      {
        title: 'Budget Management',
        description: 'Manage your overall ad spend to ensure efficiency and effective scaling.',
      },
      {
        title: 'Conversion Tracking',
        description: 'Ensure conversion tracking is correctly set up and reporting accurate data.',
      },
      {
        title: 'Transparent Reporting',
        description: 'Provide transparent, easy-to-understand performance reports and proactive recommendations.',
      },
    ],
  },
  'google-tag-manager': {
    name: 'Google Tag Manager',
    logo: 'https://www.gstatic.com/analytics-suite/header/suite/v2/ic_tag_manager.svg',
    tagline: 'Seamless and reliable data collection for your business',
    description: 'Google Tag Manager (GTM) is a free tool from Google that lets you manage all your website tracking tags, marketing pixels, and analytics scripts from a single dashboard without touching the source code.',
    iconColor: '#4285F4', // BLUE for checklist icons
    features: [
      {
        title: 'Container Setup',
        description: 'Install and configure the core GTM container on your website.',
      },
      {
        title: 'Pixel Migration',
        description: 'Migrate all existing marketing pixels (GA4, Google Ads, etc.) into the GTM container.',
      },
      {
        title: 'Data Layer Setup',
        description: 'Establish a robust Data Layer to pass precise e-commerce & user data.',
      },
      {
        title: 'E-commerce Tracking',
        description: 'Implement Enhanced E-commerce tracking for accurate sales and product performance data.',
      },
      {
        title: 'Custom Events',
        description: 'Set up custom events (e.g., button clicks, scroll depth) not tracked by default.',
      },
      {
        title: 'Third-Party Integration',
        description: 'Deploy advanced custom code and third-party tags (e.g., calendly, etc).',
      },
      {
        title: 'GDPR Compliance',
        description: 'Configure Consent Mode and privacy settings to ensure GDPR/CCPA compliance.',
      },
      {
        title: 'Quality Assurance',
        description: 'Debug and test all new tags in Preview mode to guarantee data accuracy before publishing.',
      },
    ],
  },
  'google-analytics': {
    name: 'Google Analytics',
    logo: 'https://www.gstatic.com/analytics-suite/header/suite/v2/ic_analytics.svg',
    tagline: 'Unlock actionable insights to understand your audience',
    description: 'Google Analytics is Google\'s free analytics platform that tracks how visitors find and interact with your website or app, giving you the data you need to understand your audience and grow your business.',
    iconColor: '#FBBC05', // YELLOW for checklist icons
    features: [
      {
        title: 'GA4 Setup & Migration',
        description: 'Execute the complete GA4 property setup and migration.',
      },
      {
        title: 'Data Quality Assurance',
        description: 'Ensure data quality and accuracy using the real-time DebugView tool.',
      },
      {
        title: 'Advanced Reporting',
        description: 'Build advanced, flexible analysis reports using the Explorations feature.',
      },
      {
        title: 'Tracking Deployment',
        description: 'Deploy the tracking code across web using Google Tag Manager or direct tags.',
      },
      {
        title: 'Event Strategy',
        description: 'Define a customized event strategy to track all key user interactions and mark some as Key Events.',
      },
      {
        title: 'Platform Integration',
        description: 'Connect Google Analytics with Google Ads and other essential marketing tools.',
      },
      {
        title: 'Privacy Controls',
        description: 'Model data gaps caused by consent restrictions using privacy controls.',
      },
      {
        title: 'Business Insights',
        description: 'Deliver reports that show how user data directly impacts business outcomes and growth.',
      },
    ],
  },
};

export const ServicePage = () => {
  const { serviceSlug } = useParams();
  const service = servicesData[serviceSlug];

  if (!service) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 py-20 text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground mb-4">Service Not Found</h1>
            <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <Button className="bg-[#EA4335] hover:bg-[#EA4335]/90 text-white">Back to Home</Button>
            </Link>
          </div>
        </main>
        <Footer />
        <FloatingWhatsApp />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{service.name} Services | MARKETYZE</title>
        <meta name="description" content={service.description} />
        <link rel="canonical" href={`https://marketyze.eu/services/${serviceSlug}`} />
        <meta property="og:title" content={`${service.name} Services | MARKETYZE`} />
        <meta property="og:description" content={service.description} />
        <meta property="og:url" content={`https://marketyze.eu/services/${serviceSlug}`} />
        <meta name="twitter:title" content={`${service.name} Services | MARKETYZE`} />
        <meta name="twitter:description" content={service.description} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: `${service.name} Services`,
          description: service.description,
          provider: {
            '@type': 'Organization',
            name: 'MARKETYZE',
            url: 'https://marketyze.eu/',
          },
          areaServed: 'Europe',
          url: `https://marketyze.eu/services/${serviceSlug}`,
        })}</script>
      </Helmet>
      <Navbar />
      
      <main className="pt-20 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Back Button */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>

          {/* Hero Section */}
          <div className="text-center mb-16">
            {/* Logo */}
            <div className="mb-6">
              <img 
                src={service.logo}
                alt={`${service.name} service logo`}
                width="80"
                height="80"
                className="w-20 h-20 mx-auto"
              />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              {service.name}
            </h1>
            <p className="text-xl text-muted-foreground mb-4">
              {service.tagline}
            </p>
            <p className="text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {service.description}
            </p>
          </div>

          {/* Features Grid */}
          <div className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground mb-8 text-center">
              What We Do
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {service.features.map((feature, index) => (
                <Card key={index} className="bg-card border-border/50 shadow-card hover:shadow-card-hover transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" 
                        style={{ backgroundColor: `${service.iconColor}15` }}
                      >
                        <CheckCircle className="h-5 w-5" style={{ color: service.iconColor }} />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-foreground mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center p-8 lg:p-12 bg-gradient-to-br from-[#4285F4]/10 to-[#34A853]/10 rounded-2xl border border-[#4285F4]/20">
            <h2 className="font-heading text-2xl font-bold text-foreground mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Schedule a free consultation to discuss how we can help you with {service.name}.
            </p>
            <Link to="/booking" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <Button className="bg-[#EA4335] hover:bg-[#EA4335]/90 text-white rounded-full px-8 py-6 text-base font-semibold">
                Book Free Consultation
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

export default ServicePage;
